import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import { Input, PasswordInput } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { restoreConfirm } from '../../api/auth';
import { extractErrorMessage } from '../../api/client';
import {
  restoreConfirmSchema,
  type RestoreConfirmInput,
} from '../../lib/validators';

export function RestoreConfirmPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const guid = params.get('guid') ?? '';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<RestoreConfirmInput>({
    resolver: zodResolver(restoreConfirmSchema),
    mode: 'onChange',
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    if (!guid) {
      setFormError(
        'Отсутствует идентификатор запроса. Попробуйте восстановить пароль заново.',
      );
      return;
    }
    try {
      await restoreConfirm({
        guid,
        code: values.code.trim(),
        password: values.password,
        passwordRepeat: values.passwordRepeat,
      });
      toast.success('Пароль изменён — войдите с новым паролем');
      navigate('/login');
    } catch (error) {
      setFormError(extractErrorMessage(error, 'Неверный код или истёк срок'));
    }
  });

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Новый пароль</h1>
        <p className="mt-1 text-sm text-text-muted">
          Введите код из письма и новый пароль.
        </p>
      </div>

      {formError && <ErrorBanner message={formError} />}

      <form className="flex flex-col gap-3" onSubmit={onSubmit} noValidate>
        <Input
          label="Код из письма"
          autoFocus
          placeholder="12345678"
          error={errors.code?.message}
          {...register('code')}
        />
        <PasswordInput
          label="Новый пароль"
          autoComplete="new-password"
          hint="Заглавная, строчная, цифра и спецсимвол"
          error={errors.password?.message}
          {...register('password')}
        />
        <PasswordInput
          label="Повторите пароль"
          autoComplete="new-password"
          error={errors.passwordRepeat?.message}
          {...register('passwordRepeat')}
        />
        <Button
          type="submit"
          fullWidth
          loading={isSubmitting}
          disabled={!isValid}
          size="lg"
        >
          Сохранить пароль
        </Button>
      </form>

      <Link
        to="/login"
        className="text-center text-sm font-medium text-brand-primary hover:underline"
      >
        Вернуться ко входу
      </Link>
    </Card>
  );
}
